import {
  Box,
  Button,
  CardAmexIcon,
  CardDiscoverIcon,
  CardMastercardIcon,
  CardVisaIcon,
  Disclaimer,
  Div,
  EditPenIconDarkGray,
  H3,
  H5,
  ModalPortal,
  P,
  PaymentCardIcon,
  TrashIconRed,
  lightTheme,
} from '@vgrubs/components';
import { useState } from 'react';
import type { PaymentCardLabel } from '../../types/api/outstandingBalance.types';
import { formatPaymentCard } from './paymentLabels';
import {
  BrandTile,
  CardRow,
  DefaultPill,
  RowAction,
} from './VDriveCardList.styled';

type Confirm = 'swap' | 'remove';

type Props = {
  /** The default card: `last4` from the vDrive answer, the brand where the
   *  screen has it (the outstanding balance), else a generic logo. */
  defaultCard: PaymentCardLabel;
  /** Null when there is no backup card. */
  backupCard: PaymentCardLabel | null;
  /** The default card is declining and the backup is covering the charges. */
  defaultDeclined: boolean;
  onEditDefault: () => void;
  /** Opens the backup card's form: adding one, or replacing it. */
  onEditBackup: () => void;
  /** Each resolves to whether it went through; the dialog closes on success. */
  onSwap: () => Promise<boolean>;
  onRemove: () => Promise<boolean>;
  busy: boolean;
};

/**
 * A location's two vDrive cards, laid out as the merchant mobile app shows
 * them, for the merchant web and the admin vDrive screens alike: every
 * charge tries the default card first and, when the bank declines it, the
 * backup for the same amount; a top-up neither covers comes from the
 * location's vGrubs balance (the blue card), then the bank (backend, Oct 2026).
 *
 * One row per card: the brand logo on a vDrive-blue tile, `**** 1234`, a
 * Default pill and a red outline on the default, a pencil on both and a bin
 * on the backup. Clicking the backup row makes it the default. Swap and
 * remove are confirmed here first (the backend charges the cards right after
 * either); the screen does the calls and owns the card forms.
 *
 * The screen leaves it out while the location is on the vGrubs vDrive card
 * or has no own card.
 */
export const VDriveCardList = ({
  defaultCard,
  backupCard,
  defaultDeclined,
  onEditDefault,
  onEditBackup,
  onSwap,
  onRemove,
  busy,
}: Props) => {
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const backupLabel = backupCard
    ? formatPaymentCard(backupCard) ?? 'this card'
    : '';

  const submit = async () => {
    const ok = await (confirm === 'swap' ? onSwap() : onRemove());
    if (ok) setConfirm(null);
  };

  return (
    <Box $flex $column $gap="12px" $p="16px 24px">
      <H5 $m="0">Credit Cards</H5>

      <Div $flex $column $gap="6px">
        <CardRow $default>
          <CardSummary card={defaultCard} />
          <DefaultPill>Default</DefaultPill>
          <RowAction
            type="button"
            aria-label="Edit the default card"
            onClick={onEditDefault}
          >
            <EditPenIconDarkGray />
          </RowAction>
        </CardRow>
        {defaultDeclined && (
          <Disclaimer $m="0" $color={lightTheme.primary}>
            This card was declined. Update it; the backup card is covering the
            charges for now.
          </Disclaimer>
        )}
      </Div>

      {backupCard ? (
        <CardRow
          $clickable
          role="button"
          tabIndex={0}
          aria-label={`Make ${backupLabel} the default card`}
          onClick={() => setConfirm('swap')}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setConfirm('swap');
            }
          }}
        >
          <CardSummary card={backupCard} />
          <RowAction
            type="button"
            aria-label="Edit the backup card"
            onClick={(event) => {
              event.stopPropagation();
              onEditBackup();
            }}
          >
            <EditPenIconDarkGray />
          </RowAction>
          <RowAction
            type="button"
            aria-label="Remove the backup card"
            onClick={(event) => {
              event.stopPropagation();
              setConfirm('remove');
            }}
          >
            <TrashIconRed />
          </RowAction>
        </CardRow>
      ) : (
        <Button $small $selected $w="100%" $wSM="100%" onClick={onEditBackup}>
          Add Backup Card
        </Button>
      )}

      <Disclaimer $m="0">
        We charge the default card. If the bank declines it, we charge the
        backup card, then the location&apos;s vGrubs balance (no fees), then the
        bank account. A declined card is tried again on the next 2 top-ups, and
        once a day until it goes through or is replaced.
      </Disclaimer>

      <ModalPortal
        isOpen={confirm !== null}
        closeModal={busy ? undefined : () => setConfirm(null)}
        logo={false}
        exitButton={false}
        width="360px"
        backgroundColor={lightTheme.white}
      >
        <Div $flex $column $alignCenter $gap="12px">
          <H3 $m="0" $center>
            {confirm === 'swap' ? 'Swap the cards?' : 'Are you sure?'}
          </H3>
          <P $m="0" $center>
            {confirm === 'swap'
              ? `Charges will try ${backupLabel} first, and the current default becomes the backup card.`
              : `Remove ${backupLabel} as the backup card? Charges will only try the default card.`}
          </P>
          <Div $flex $gap="12px" $width="100%" $mt="12">
            <Button
              $selected
              $gray
              $w="100%"
              $wSM="100%"
              disabled={busy}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              $selected
              $w="100%"
              $wSM="100%"
              $loading={busy}
              disabled={busy}
              onClick={submit}
            >
              {confirm === 'swap' ? 'Swap' : 'Remove'}
            </Button>
          </Div>
        </Div>
      </ModalPortal>
    </Box>
  );
};

/** The logo tile and `**** 1234`, the part both rows share. */
const CardSummary = ({ card }: { card: PaymentCardLabel }) => (
  <Div $flex $alignCenter $gap="12px" $width="100%">
    <BrandTile>
      <CardBrandLogo brand={card.brand} />
    </BrandTile>
    <P $m="0">**** {card.last4 ?? ''}</P>
  </Div>
);

/**
 * A card's brand logo from the brand as Stripe spells it (`visa`,
 * `mastercard`, `amex`, `discover`, `unionpay`...), or null on older cards;
 * anything without a logo gets the generic card. White on the Credit Cards
 * tiles, dark blue on the pay screen's card tile.
 */
export const CardBrandLogo = ({
  brand,
  color = lightTheme.white,
}: {
  brand: string | null | undefined;
  color?: string;
}) => {
  switch (brand?.toLowerCase()) {
    case 'visa':
      return <CardVisaIcon color={color} />;
    case 'mastercard':
      return <CardMastercardIcon color={color} />;
    case 'amex':
    case 'american express':
      return <CardAmexIcon color={color} />;
    case 'discover':
      return <CardDiscoverIcon color={color} />;
    default:
      return <PaymentCardIcon color={color} />;
  }
};
